// frontend/src/screens/ProfileScreen.jsx
import { useState, useEffect, useMemo } from 'react';
import { Table, Form, Button, Row, Col, Badge, OverlayTrigger, Tooltip, Card, Stack, Alert, InputGroup, ProgressBar, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaTimes, FaSearch, FaRegClock, FaShieldAlt, FaBoxOpen, FaCheckCircle,
  FaFilePdf, FaCreditCard, FaChevronDown, FaChevronUp, FaExclamationTriangle,
  FaSlidersH,
} from 'react-icons/fa';

import Message from '../Components/Message';
import Loader from '../Components/Loader';
import { useProfileMutation } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authSlice';
import { useGetMyOrdersQuery } from '../slices/ordersApiSlice';
import { useGetMyStatementsQuery } from '../slices/eomApiSlice';
import BackButton from "../Components/BackButton";
import { PICKUP_OPTIONS, CA_PROVINCES } from '../utils/shippingPolicy';
import { loadPrefs, savePrefs } from '../utils/orderPreferences';
import './ProfileScreen.css';

const POLICY_DAYS = 30;

/* ----------------------- helpers ----------------------- */

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function daysLeft(baseISO) {
  if (!baseISO) return 0;
  const end = new Date(new Date(baseISO).getTime() + POLICY_DAYS * 24 * 60 * 60 * 1000);
  const diff = Math.ceil((end.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  return clamp(diff, -9999, 9999);
}

function isEomOrder(order) {
  const paymentMethod = String(order?.paymentMethod || '').toLowerCase();

  return (
    paymentMethod.includes('eom') ||
    paymentMethod.includes('monthly') ||
    paymentMethod.includes('end of month') ||
    order?.user?.endOfMonthPaymentEnabled === true
  );
}

function isOrderCompletedEnoughForEomReturn(order) {
  const status = String(order?.status || '').toLowerCase();

  return (
    order?.isDelivered === true ||
    !!order?.deliveredAt ||
    !!order?.collectedAt ||
    status === 'delivered' ||
    status === 'collected' ||
    status === 'completed'
  );
}

function isEligibleForReturn(order) {
  if (!order) return false;
  if (order.isCancelled) return false;
  if (String(order.status || '').toLowerCase() === 'cancelled') return false;

  const eomOrder = isEomOrder(order);

  if (!eomOrder && !order.isPaid) return false;
  if (eomOrder && !isOrderCompletedEnoughForEomReturn(order)) return false;

  const base = order.deliveredAt || order.collectedAt || order.paidAt || order.createdAt;
  return daysLeft(base) > 0;
}

function returnUnavailableLabel(order) {
  if (hasOpenReturn(order)) return 'Requested';
  if (order?.isCancelled || String(order?.status || '').toLowerCase() === 'cancelled') {
    return 'Cancelled';
  }
  const eomOrder = isEomOrder(order);
  if (eomOrder && !isOrderCompletedEnoughForEomReturn(order)) return 'Awaiting delivery';
  if (!eomOrder && !order?.isPaid) return 'Not paid';
  return 'Past window';
}

function displayInvoiceOrRef(order) {
  const inv = order?.documentRefs?.invoiceNumber || order?.invoice || null;
  if (inv) return inv;

  const ref = order?.orderRef || order?.reference || '';
  if (ref) return ref;

  return order?._id ? `…${String(order._id).slice(-6)}` : '—';
}

// 🟢 ADDED: derive whether this order has any *open* return requests
function hasOpenReturn(order) {
  // Option A: backend provides condensed status
  const status = String(order?.returnStatus || '').trim().toLowerCase();
  if (status && status !== 'none' && status !== 'closed' && status !== 'refunded') return true;

  // Option B: backend attached returns array on the order
  if (Array.isArray(order?.returns)) {
    return order.returns.some((r) =>
      ['requested', 'authorized', 'received', 'partial_refunded'].includes(String(r.status).toLowerCase())
    );
  }
  return false;
}

// 🟢 ADDED: pretty label for statuses if you ever pass raw schema values through
function prettyReturnStatus(s) {
  const v = String(s || '').toLowerCase();
  switch (v) {
    case 'requested':
      return 'Requested';
    case 'authorized':
      return 'Authorized';
    case 'rejected':
      return 'Rejected';
    case 'received':
      return 'Returned';
    case 'refunded':
      return 'Refunded';
    case 'partial_refunded':
      return 'Partial refund';
    case 'closed':
      return 'Closed';
    default:
      return s || 'Requested';
  }
}

// 🟢 ADDED: map to Bootstrap badge colors
function statusColor(s) {
  const v = String(s || '').toLowerCase();
  if (v === 'requested') return 'warning';        // yellow = action pending
  if (v === 'authorized') return 'primary';
  if (v === 'rejected') return 'danger';
  if (v === 'received') return 'secondary';
  if (v === 'partial_refunded') return 'info';
  if (v === 'refunded' || v === 'closed') return 'success';
  return 'secondary';
}

function getInitials(nameValue, emailValue) {
  const source = String(nameValue || '').trim();
  if (source) {
    return source
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  }
  return String(emailValue || 'U')
    .trim()
    .slice(0, 2)
    .toUpperCase();
}

function getPasswordStrengthLabel(passwordValue) {
  if (!passwordValue) return { label: 'No password change', score: 0, variant: 'secondary' };

  const hasLength = passwordValue.length >= 8;
  const hasUpper = /[A-Z]/.test(passwordValue);
  const hasLower = /[a-z]/.test(passwordValue);
  const hasNumber = /[0-9]/.test(passwordValue);
  const hasSymbol = /[^A-Za-z0-9]/.test(passwordValue);
  const score = [hasLength, hasUpper, hasLower, hasNumber, hasSymbol].filter(Boolean).length;

  if (score >= 5) return { label: 'Strong', score: 100, variant: 'success' };
  if (score >= 3) return { label: 'Moderate', score: 60, variant: 'warning' };
  return { label: 'Weak', score: 30, variant: 'danger' };
}

/* ----------------------- component ----------------------- */

export default function ProfileScreen() {
  // form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderFilter, setOrderFilter] = useState('all');
  const [pendingOrdersOpen, setPendingOrdersOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');

  // Checkout preference state
  const [prefFulfillment, setPrefFulfillment] = useState('ship');
  const [prefPickupLocation, setPrefPickupLocation] = useState(PICKUP_OPTIONS[0].value);
  const [prefPhone, setPrefPhone] = useState('');
  const [prefPaymentMethod, setPrefPaymentMethod] = useState('PayPal');
  const [prefAddress, setPrefAddress] = useState('');
  const [prefAddress2, setPrefAddress2] = useState('');
  const [prefCity, setPrefCity] = useState('');
  const [prefProvince, setPrefProvince] = useState('ON');
  const [prefPostalCode, setPrefPostalCode] = useState('');
  const [prefCountry, setPrefCountry] = useState('CA');

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);

  const [updateProfile, { isLoading: loadingUpdateProfile }] = useProfileMutation();
  const { data: ordersRaw = [], isLoading, error } = useGetMyOrdersQuery();
  const { data: statementsData, isLoading: loadingStatements } = useGetMyStatementsQuery(
    undefined,
    { skip: !userInfo?.endOfMonthPaymentEnabled }
  );

  // 🔴 CHANGED: keep the table stable (newest first)
  const orders = useMemo(
    () => [...ordersRaw].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [ordersRaw]
  );

  const orderStats = useMemo(() => {
    const total = orders.length;
    const delivered = orders.filter((order) => order.isDelivered).length;
    const paid = orders.filter((order) => order.isPaid).length;
    return { total, delivered, paid };
  }, [orders]);

  const passwordStrength = useMemo(() => getPasswordStrengthLabel(password), [password]);

  const filteredOrders = useMemo(() => {
    const query = orderSearch.trim().toLowerCase();
    return orders.filter((order) => {
      const eligible = isEligibleForReturn(order);
      const hasOpen = hasOpenReturn(order);
      const invoiceOrRef = displayInvoiceOrRef(order).toLowerCase();
      const matchesSearch =
        !query ||
        invoiceOrRef.includes(query) ||
        String(order._id || '').toLowerCase().includes(query);

      if (!matchesSearch) return false;

      if (orderFilter === 'delivered') return order.isDelivered;
      if (orderFilter === 'pending') return !order.isDelivered;
      if (orderFilter === 'returnEligible') return eligible && !hasOpen;
      if (orderFilter === 'returnsInProgress') return hasOpen;

      return true;
    });
  }, [orders, orderSearch, orderFilter]);

  const paidPct = orderStats.total ? Math.round((orderStats.paid / orderStats.total) * 100) : 0;
  const deliveredPct = orderStats.total ? Math.round((orderStats.delivered / orderStats.total) * 100) : 0;

  useEffect(() => {
    if (userInfo) {
      setName(userInfo.name || '');
      setEmail(userInfo.email || '');
    }
  }, [userInfo]);

  // Load saved checkout preferences when user is known
  useEffect(() => {
    if (!userInfo?._id) return;
    const p = loadPrefs(userInfo._id);
    if (p.defaultFulfillmentMethod) setPrefFulfillment(p.defaultFulfillmentMethod);
    if (p.defaultPickupLocation)    setPrefPickupLocation(p.defaultPickupLocation);
    if (p.defaultPhone)             setPrefPhone(p.defaultPhone);
    if (p.defaultPaymentMethod)     setPrefPaymentMethod(p.defaultPaymentMethod);
    const addr = p.defaultShippingAddress || {};
    setPrefAddress(addr.address   || '');
    setPrefAddress2(addr.address2 || '');
    setPrefCity(addr.city         || '');
    setPrefProvince(addr.province || 'ON');
    setPrefPostalCode(addr.postalCode || '');
    setPrefCountry(addr.country   || 'CA');
  }, [userInfo?._id]);

  const savePrefsHandler = () => {
    if (!userInfo?._id) return;
    savePrefs(userInfo._id, {
      defaultFulfillmentMethod: prefFulfillment,
      defaultPickupLocation:    prefPickupLocation,
      defaultPhone:             prefPhone.trim(),
      defaultPaymentMethod:     prefPaymentMethod,
      defaultShippingAddress:   prefFulfillment === 'ship' ? {
        address:    prefAddress.trim(),
        address2:   prefAddress2.trim(),
        city:       prefCity.trim(),
        province:   prefProvince.trim().toUpperCase(),
        postalCode: prefPostalCode.trim().replace(/\s+/g, '').toUpperCase(),
        country:    prefCountry.trim().toUpperCase(),
      } : {},
    });
    toast.success('Preferences saved');
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      const res = await updateProfile({
        _id: userInfo._id,
        name,
        email,
        password: password || undefined,
      }).unwrap();
      dispatch(setCredentials(res));
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Update failed');
    }
  };

  return (
    <>
      <BackButton />

      {/* ── Profile Header ── */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {/* Avatar */}
            <div
              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 52, height: 52, fontSize: 18, fontWeight: 600 }}
            >
              {getInitials(name, email)}
            </div>

            {/* Name + email */}
            <div className="flex-grow-1">
              <div className="fw-semibold fs-5 mb-0">{userInfo?.name || 'Your Account'}</div>
              <div className="text-muted small">{userInfo?.email}</div>
            </div>

            {/* Order stats */}
            <Stack direction="horizontal" gap={0} className="d-none d-sm-flex">
              <div className="text-center px-3 border-end">
                <div className="fw-bold fs-5">{orderStats.total}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>ORDERS</div>
              </div>
              <div className="text-center px-3 border-end">
                <div className="fw-bold fs-5 text-success">{orderStats.delivered}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>DELIVERED</div>
              </div>
              <div className="text-center px-3">
                <div className="fw-bold fs-5 text-primary">{orderStats.paid}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>PAID</div>
              </div>
            </Stack>

            {/* EOM balance chip — clickable shortcut to billing tab */}
            {userInfo?.endOfMonthPaymentEnabled && !loadingStatements && (() => {
              const unpaidStmts = (statementsData?.statements || []).filter((s) => !s.isPaid);
              const hasOverdue = unpaidStmts.some((s) => s.dueDate && new Date(s.dueDate) < new Date());
              const owed = unpaidStmts.reduce((sum, s) => sum + Number(s.total || 0), 0);
              if (!owed) return null;
              return (
                <div
                  role="button"
                  tabIndex={0}
                  className={`px-3 py-2 rounded-3 text-center ${hasOverdue ? 'bg-danger text-white' : 'bg-warning bg-opacity-25'}`}
                  style={{ minWidth: 110, cursor: 'pointer' }}
                  onClick={() => setActiveTab('billing')}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveTab('billing')}
                >
                  <div className="fw-bold">${owed.toFixed(2)}</div>
                  <div style={{ fontSize: 11 }}>{hasOverdue ? 'OVERDUE' : 'OUTSTANDING'}</div>
                </div>
              );
            })()}
          </div>
        </Card.Body>
      </Card>

      {/* ── Tab Navigation ── */}
      <Nav
        variant="pills"
        className="mb-4"
        activeKey={activeTab}
        onSelect={(k) => k && setActiveTab(k)}
      >
        <Nav.Item>
          <Nav.Link eventKey="orders" className="me-2">My Orders</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="account" className="me-2">Account Settings</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="preferences" className="me-2">
            <FaSlidersH className="me-1" />Preferences
          </Nav.Link>
        </Nav.Item>
        {userInfo?.endOfMonthPaymentEnabled && (
          <Nav.Item>
            <Nav.Link eventKey="billing">
              Monthly Billing
              {!loadingStatements && (() => {
                const unpaidStmts = (statementsData?.statements || []).filter((s) => !s.isPaid);
                if (!unpaidStmts.length) return null;
                const hasOverdue = unpaidStmts.some((s) => s.dueDate && new Date(s.dueDate) < new Date());
                return (
                  <Badge
                    bg={hasOverdue ? 'danger' : 'warning'}
                    text={hasOverdue ? undefined : 'dark'}
                    className="ms-2"
                  >
                    {unpaidStmts.length}
                  </Badge>
                );
              })()}
            </Nav.Link>
          </Nav.Item>
        )}
      </Nav>

      {/* ══════════════════════════════════════════════
          TAB: My Orders
      ══════════════════════════════════════════════ */}
      {activeTab === 'orders' && (
        <Card className="shadow-sm border-0">
          <Card.Body>
            <Row className="g-2 mb-3">
              <Col sm={6}>
                <div className="profile-surface p-3 h-100 rounded-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Payment Completion</small>
                    <FaCheckCircle className="text-primary" />
                  </div>
                  <h4 className="mb-2">{paidPct}%</h4>
                  <ProgressBar now={paidPct} variant="primary" style={{ height: 8 }} />
                </div>
              </Col>
              <Col sm={6}>
                <div className="profile-surface p-3 h-100 rounded-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Delivery Completion</small>
                    <FaBoxOpen className="text-success" />
                  </div>
                  <h4 className="mb-2">{deliveredPct}%</h4>
                  <ProgressBar now={deliveredPct} variant="success" style={{ height: 8 }} />
                </div>
              </Col>
            </Row>

            <Alert variant="info" className="mb-3">
              Returns are eligible within <strong>{POLICY_DAYS} days</strong> of delivery.
            </Alert>

            <div className="profile-surface rounded-3 p-3 mb-3">
              <Row className="g-2 align-items-center">
                <Col lg={5}>
                  <InputGroup>
                    <InputGroup.Text><FaSearch /></InputGroup.Text>
                    <Form.Control
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      placeholder="Search by invoice, ref, or order id"
                    />
                  </InputGroup>
                </Col>
                <Col lg={7}>
                  <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                    {[
                      ['all', 'All'],
                      ['delivered', 'Delivered'],
                      ['pending', 'Pending Delivery'],
                      ['returnEligible', 'Return Eligible'],
                      ['returnsInProgress', 'Returns In Progress'],
                    ].map(([value, label]) => (
                      <Button
                        key={value}
                        size="sm"
                        variant={orderFilter === value ? 'dark' : 'outline-dark'}
                        onClick={() => setOrderFilter(value)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </Col>
              </Row>
            </div>

            {isLoading ? (
              <Loader />
            ) : error ? (
              <Message variant="danger">{error?.data?.message || error.error}</Message>
            ) : filteredOrders.length === 0 ? (
              <Alert variant="light" className="border text-center py-4">
                No orders match your current filters.
              </Alert>
            ) : (
              <Table striped hover responsive className="table-sm">
                <thead>
                  <tr>
                    <th>INVOICE / REF</th>
                    <th>DATE</th>
                    <th>TOTAL</th>
                    <th>PAID</th>
                    <th>DELIVERED</th>
                    <th>RETURN</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const eligible = isEligibleForReturn(order);
                    const base = order?.deliveredAt || order?.collectedAt || order?.paidAt || order?.createdAt;
                    const left = daysLeft(base);
                    const rawStatus = String(order?.returnStatus || '').trim().toLowerCase();
                    const open = hasOpenReturn(order);

                    return (
                      <tr key={order._id}>
                        <td className="text-truncate" style={{ maxWidth: 220 }}>
                          {displayInvoiceOrRef(order)}
                        </td>
                        <td>{order.createdAt ? order.createdAt.substring(0, 10) : 'N/A'}</td>
                        <td>${Number(order.totalPrice ?? 0).toFixed(2)}</td>
                        <td>
                          {order.isPaid && order.paidAt ? (
                            order.paidAt.substring(0, 10)
                          ) : (
                            <FaTimes style={{ color: 'red' }} />
                          )}
                        </td>
                        <td>
                          {order.isDelivered && order.deliveredAt ? (
                            order.deliveredAt.substring(0, 10)
                          ) : (
                            <FaTimes style={{ color: 'red' }} />
                          )}
                        </td>
                        <td>
                          {open ? (
                            <>
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`tt-${order._id}`}>A return is in progress</Tooltip>}
                              >
                                <Badge bg={statusColor(rawStatus)} className="text-uppercase me-2">
                                  {prettyReturnStatus(rawStatus || 'requested')}
                                </Badge>
                              </OverlayTrigger>
                              <Link to={`/order/${order._id}`} className="btn btn-outline-dark btn-sm">
                                View
                              </Link>
                            </>
                          ) : eligible ? (
                            <div className="d-flex align-items-center gap-2">
                              <Link
                                to={`/return-request/${order._id}`}
                                className="btn btn-success btn-sm fw-semibold"
                                style={{ minWidth: 140 }}
                              >
                                Request Return
                              </Link>
                              {left > 0 && (
                                <span className="text-success small">{left}d left</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted small">{returnUnavailableLabel(order)}</span>
                          )}
                        </td>
                        <td>
                          <Link to={`/order/${order._id}`}>
                            <Button className="btn-sm" variant="light">Details</Button>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ══════════════════════════════════════════════
          TAB: Account Settings
      ══════════════════════════════════════════════ */}
      {activeTab === 'account' && (
        <Row className="g-4">
          <Col md={7}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h5 className="mb-4">Profile Details</h5>
                <Form onSubmit={submitHandler}>
                  <Form.Group controlId="name" className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group controlId="email" className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <Form.Text muted>We'll use this for receipts and order updates.</Form.Text>
                  </Form.Group>

                  <Form.Group controlId="password" className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Leave blank to keep your current password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <div className="d-flex justify-content-between mt-2 align-items-center">
                      <Form.Text muted>Use 8+ chars, with numbers and symbols.</Form.Text>
                      <Badge bg={passwordStrength.variant}>{passwordStrength.label}</Badge>
                    </div>
                    <ProgressBar
                      className="mt-2"
                      now={passwordStrength.score}
                      variant={passwordStrength.variant}
                      style={{ height: 6 }}
                    />
                  </Form.Group>

                  <Form.Group controlId="confirmPassword" className="mb-3">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </Form.Group>

                  <Button type="submit" variant="primary" className="w-100">
                    Save Changes
                  </Button>
                  {loadingUpdateProfile && <Loader />}
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col md={5}>
            <Card className="shadow-sm border-0 mb-3">
              <Card.Body>
                <h5 className="mb-3">Account Pricing</h5>
                <div className="text-muted small">
                  Your account-specific pricing will be applied automatically at checkout.
                </div>
              </Card.Body>
            </Card>

            <Alert variant="light" className="border-0 shadow-sm profile-surface">
              <div className="d-flex align-items-start gap-2">
                <FaShieldAlt className="text-primary mt-1" />
                <div>
                  <strong>Security tip:</strong> Use a unique password and update it regularly.
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* ══════════════════════════════════════════════
          TAB: Preferences
      ══════════════════════════════════════════════ */}
      {activeTab === 'preferences' && (
        <Row className="g-4">
          {/* Left column — delivery & address */}
          <Col md={7}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h5 className="mb-1">Checkout Defaults</h5>
                <p className="text-muted small mb-4">
                  These pre-fill the checkout form so you spend less time typing on each order.
                  You can always change them at checkout.
                </p>

                {/* Default delivery method */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Default Delivery Method</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      id="pref-ship"
                      name="prefFulfillment"
                      label="Ship to address"
                      value="ship"
                      checked={prefFulfillment === 'ship'}
                      onChange={() => setPrefFulfillment('ship')}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      id="pref-pickup"
                      name="prefFulfillment"
                      label="In-Store Pickup"
                      value="pickup"
                      checked={prefFulfillment === 'pickup'}
                      onChange={() => setPrefFulfillment('pickup')}
                    />
                  </div>
                </Form.Group>

                {/* Default pickup location (only when pickup is default) */}
                {prefFulfillment === 'pickup' && (
                  <Form.Group controlId="prefPickupLocation" className="mb-4">
                    <Form.Label className="fw-semibold">Default Pickup Location</Form.Label>
                    <Form.Select
                      value={prefPickupLocation}
                      onChange={(e) => setPrefPickupLocation(e.target.value)}
                    >
                      {PICKUP_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                {/* Default phone */}
                <Form.Group controlId="prefPhone" className="mb-4">
                  <Form.Label className="fw-semibold">Default Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="e.g., 613-555-0123"
                    value={prefPhone}
                    onChange={(e) => setPrefPhone(e.target.value)}
                  />
                  <Form.Text muted>Pre-filled in the contact section at checkout.</Form.Text>
                </Form.Group>

                {/* Default shipping address (only when ship is default) */}
                {prefFulfillment === 'ship' && (
                  <>
                    <hr />
                    <p className="fw-semibold mb-3">Default Shipping Address</p>

                    <Form.Group controlId="prefAddress" className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Street address"
                        value={prefAddress}
                        onChange={(e) => setPrefAddress(e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group controlId="prefAddress2" className="mb-3">
                      <Form.Label>Unit / Apt / Suite <span className="text-muted">(optional)</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Apt 3B, Unit 12"
                        value={prefAddress2}
                        onChange={(e) => setPrefAddress2(e.target.value)}
                      />
                    </Form.Group>

                    <Row>
                      <Col sm={6}>
                        <Form.Group controlId="prefCity" className="mb-3">
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="City"
                            value={prefCity}
                            onChange={(e) => setPrefCity(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="prefProvince" className="mb-3">
                          <Form.Label>Province / State</Form.Label>
                          {prefCountry.toUpperCase() === 'CA' ? (
                            <Form.Select
                              value={prefProvince}
                              onChange={(e) => setPrefProvince(e.target.value)}
                            >
                              <option value="">Select province</option>
                              {CA_PROVINCES.map((p) => (
                                <option key={p.code} value={p.code}>{p.name}</option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              type="text"
                              placeholder="e.g., NY"
                              value={prefProvince}
                              onChange={(e) => setPrefProvince(e.target.value.toUpperCase())}
                            />
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col sm={6}>
                        <Form.Group controlId="prefPostalCode" className="mb-3">
                          <Form.Label>Postal / ZIP Code</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., K2G 4M9"
                            value={prefPostalCode}
                            onChange={(e) => setPrefPostalCode(e.target.value.toUpperCase())}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="prefCountry" className="mb-3">
                          <Form.Label>Country Code</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="CA"
                            value={prefCountry}
                            onChange={(e) => setPrefCountry(e.target.value.toUpperCase())}
                            maxLength={3}
                          />
                          <Form.Text muted>CA, US, etc.</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}

                <Button variant="primary" onClick={savePrefsHandler} className="w-100 mt-1">
                  Save Preferences
                </Button>
              </Card.Body>
            </Card>
          </Col>

          {/* Right column — payment preference */}
          <Col md={5}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h5 className="mb-1">Preferred Payment Method</h5>
                <p className="text-muted small mb-3">
                  Your preferred method will be pre-selected at checkout when it's available
                  for your order type.
                </p>

                <Form.Check
                  type="radio"
                  id="pref-pm-paypal"
                  name="prefPaymentMethod"
                  label="PayPal or Credit Card"
                  value="PayPal"
                  checked={prefPaymentMethod === 'PayPal'}
                  onChange={() => setPrefPaymentMethod('PayPal')}
                  className="mb-2"
                />

                <Form.Check
                  type="radio"
                  id="pref-pm-pickup"
                  name="prefPaymentMethod"
                  label="Pay on Pickup"
                  value="PickupPay"
                  checked={prefPaymentMethod === 'PickupPay'}
                  onChange={() => setPrefPaymentMethod('PickupPay')}
                  className="mb-2"
                />

                {userInfo?.tier && (
                  <Form.Check
                    type="radio"
                    id="pref-pm-delivery"
                    name="prefPaymentMethod"
                    label="Payment at Delivery"
                    value="Payment at Delivery"
                    checked={prefPaymentMethod === 'Payment at Delivery'}
                    onChange={() => setPrefPaymentMethod('Payment at Delivery')}
                    className="mb-2"
                  />
                )}

                {userInfo?.endOfMonthPaymentEnabled && (
                  <Form.Check
                    type="radio"
                    id="pref-pm-eom"
                    name="prefPaymentMethod"
                    label="End of Month Payment"
                    value="End of Month Payment"
                    checked={prefPaymentMethod === 'End of Month Payment'}
                    onChange={() => setPrefPaymentMethod('End of Month Payment')}
                    className="mb-2"
                  />
                )}

                <Alert variant="light" className="border mt-3 small mb-0">
                  Actual availability is confirmed at checkout based on order type and
                  account tier. If your preferred method isn't available, you'll need to
                  select one manually.
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* ══════════════════════════════════════════════
          TAB: Monthly Billing (EOM only)
      ══════════════════════════════════════════════ */}
      {activeTab === 'billing' && userInfo?.endOfMonthPaymentEnabled && (() => {
        if (loadingStatements) return <Card className="shadow-sm border-0"><Card.Body><Loader /></Card.Body></Card>;

        const allStatements   = statementsData?.statements || [];
        const pendingOrders   = statementsData?.pendingOrders || [];
        const pendingOrdTotal = statementsData?.pendingTotal || 0;
        const unpaid   = allStatements.filter((s) => !s.isPaid);
        const paid     = allStatements.filter((s) => s.isPaid);
        const overdue  = unpaid.filter((s) => s.dueDate && new Date(s.dueDate) < new Date());
        const totalOwed = unpaid.reduce((sum, s) => sum + Number(s.total || 0), 0) + Number(pendingOrdTotal);

        const fmtMonth = (m) => {
          try { return new Date(`${m}-01`).toLocaleString('en-CA', { month: 'long', year: 'numeric' }); }
          catch { return m; }
        };
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-CA') : '—';

        return (
          <div>
            {/* Balance Banner */}
            {overdue.length > 0 ? (
              <Alert variant="danger" className="d-flex align-items-start gap-3 shadow-sm border-0 mb-3" style={{ borderRadius: 12 }}>
                <FaExclamationTriangle style={{ fontSize: 22, marginTop: 2, flexShrink: 0 }} />
                <div className="flex-grow-1">
                  <strong>Overdue balance — action required</strong>
                  <div className="small mt-1">
                    You have {overdue.length} overdue statement{overdue.length > 1 ? 's' : ''}.
                    Please pay as soon as possible or contact us.
                  </div>
                </div>
                <div className="text-end" style={{ flexShrink: 0 }}>
                  <div className="fw-bold fs-5">${totalOwed.toFixed(2)}</div>
                  <div className="small">total outstanding</div>
                </div>
              </Alert>
            ) : totalOwed > 0 ? (
              <Alert variant="warning" className="d-flex align-items-center gap-3 shadow-sm border-0 mb-3" style={{ borderRadius: 12 }}>
                <FaRegClock style={{ fontSize: 20, flexShrink: 0 }} />
                <div className="flex-grow-1">
                  <strong>Outstanding balance</strong>
                  <div className="small mt-1">You have {unpaid.length} unpaid statement{unpaid.length !== 1 ? 's' : ''}.</div>
                </div>
                <div className="text-end fw-bold fs-5" style={{ flexShrink: 0 }}>${totalOwed.toFixed(2)}</div>
              </Alert>
            ) : allStatements.length > 0 ? (
              <Alert variant="success" className="d-flex align-items-center gap-3 shadow-sm border-0 mb-3" style={{ borderRadius: 12 }}>
                <FaCheckCircle style={{ fontSize: 20, flexShrink: 0 }} />
                <div>
                  <strong>Your account is up to date</strong>
                  <div className="small mt-1">No outstanding balances.</div>
                </div>
              </Alert>
            ) : null}

            {/* Unpaid Statements */}
            {unpaid.length > 0 && (
              <Card className="shadow-sm border-0 mb-3">
                <Card.Header className="bg-transparent border-bottom py-3">
                  <h5 className="mb-0">Statements Due</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  {unpaid.map((stmt, i) => {
                    const isOverdueStmt = stmt.dueDate && new Date(stmt.dueDate) < new Date();
                    return (
                      <div
                        key={stmt._id}
                        className={`d-flex flex-wrap align-items-center gap-3 p-3${i < unpaid.length - 1 ? ' border-bottom' : ''}${isOverdueStmt ? ' bg-danger bg-opacity-10' : ''}`}
                      >
                        <div style={{ minWidth: 140 }}>
                          <div className="fw-semibold">{fmtMonth(stmt.month)}</div>
                          <div className="text-muted small">{stmt.statementNumber || '—'}</div>
                        </div>
                        <div style={{ minWidth: 90 }}>
                          <div className="fw-bold fs-5">${Number(stmt.total || 0).toFixed(2)}</div>
                          <div className="text-muted small">CAD</div>
                        </div>
                        <div style={{ minWidth: 120 }}>
                          {isOverdueStmt ? (
                            <Badge bg="danger" className="mb-1">Overdue</Badge>
                          ) : (
                            <Badge bg="warning" text="dark" className="mb-1">Pending</Badge>
                          )}
                          {stmt.dueDate && (
                            <div className={`small ${isOverdueStmt ? 'text-danger fw-semibold' : 'text-muted'}`}>
                              Due {fmtDate(stmt.dueDate)}
                            </div>
                          )}
                        </div>
                        <div className="d-flex gap-2 ms-auto flex-wrap">
                          <a
                            href={`/api/eom/statements/${stmt._id}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-secondary btn-sm"
                            title="Download Statement PDF"
                          >
                            <FaFilePdf className="me-1" /> PDF
                          </a>
                          <Link
                            to={`/eom-statement/${stmt._id}/pay`}
                            className={`btn btn-sm fw-semibold${isOverdueStmt ? ' btn-danger' : ' btn-primary'}`}
                          >
                            <FaCreditCard className="me-1" /> Pay Now
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </Card.Body>
              </Card>
            )}

            {/* Accumulating Orders */}
            {pendingOrders.length > 0 && (
              <Card className="shadow-sm border-0 mb-3">
                <Card.Header
                  className="bg-transparent border-bottom py-3 d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setPendingOrdersOpen((o) => !o)}
                >
                  <div>
                    <h5 className="mb-0">
                      Accumulating Orders
                      <Badge bg="secondary" className="ms-2">{pendingOrders.length}</Badge>
                    </h5>
                    <div className="text-muted small mt-1">
                      Orders not yet included in a statement — total{' '}
                      <strong>${Number(pendingOrdTotal).toFixed(2)}</strong>
                    </div>
                  </div>
                  {pendingOrdersOpen ? <FaChevronUp /> : <FaChevronDown />}
                </Card.Header>
                {pendingOrdersOpen && (
                  <Card.Body className="p-0">
                    <Table hover responsive className="table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>DATE</th>
                          <th>INVOICE / REF</th>
                          <th className="text-end">AMOUNT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingOrders.map((o) => (
                          <tr key={o._id}>
                            <td className="text-muted small">
                              {o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-CA') : '—'}
                            </td>
                            <td>{o.invoice || o.reference || '—'}</td>
                            <td className="text-end">${Number(o.totalPrice || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-light">
                          <td colSpan={2} className="fw-semibold">Subtotal</td>
                          <td className="text-end fw-semibold">${Number(pendingOrdTotal).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </Table>
                  </Card.Body>
                )}
              </Card>
            )}

            {/* Payment History */}
            {paid.length > 0 && (
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-transparent border-bottom py-3">
                  <h5 className="mb-0">Payment History</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>STATEMENT</th>
                        <th>MONTH</th>
                        <th>AMOUNT PAID</th>
                        <th>DATE PAID</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paid.map((stmt) => (
                        <tr key={stmt._id}>
                          <td className="text-muted small">{stmt.statementNumber || '—'}</td>
                          <td>{fmtMonth(stmt.month)}</td>
                          <td className="fw-semibold">${Number(stmt.paidAmount ?? stmt.total ?? 0).toFixed(2)}</td>
                          <td className="text-muted small">{fmtDate(stmt.paidAt)}</td>
                          <td>
                            <a
                              href={`/api/eom/statements/${stmt._id}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-secondary btn-sm"
                              title="Download PDF"
                            >
                              <FaFilePdf />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Empty state */}
            {allStatements.length === 0 && pendingOrders.length === 0 && (
              <Card className="shadow-sm border-0">
                <Card.Body className="text-center py-5 text-muted">
                  <FaRegClock style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }} />
                  <div>No statements yet. Place orders and your account manager will issue monthly statements.</div>
                </Card.Body>
              </Card>
            )}
          </div>
        );
      })()}
    </>
  );
}
